'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Color } from '@bytel/product-wall';
import { classNames } from '@bytel/product-wall/helpers';
import {
    Align,
    Button,
    Column,
    Columns,
    DirectionEnum,
    FlexBox,
    GapSize,
    IconName,
    Justify,
    Spacer,
    SpacerSize,
    Text,
    TextMarkup,
    View,
} from '@bytel/trilogy-react';

import { WallType } from '@app-types/config';
import { ProductChildFull, ProductParentFull } from '@app-types/product';
import { Product3dViewer } from '@app-types/product-3d';

import { EnergyLabel } from '@components/energy-label';
import { ImageThumbor } from '@components/image-thumbor';

import { PRODUCT_3D_VIEWER_MESSAGES } from '@constants/product-3d';

import { Das } from '@product-detail/components';
import { sendProductPreviewTracking, sendViewer3dOpeningTracking } from '@product-detail/helpers';

export type ProductPreviewProps = {
    type: WallType;
    features: ProductParentFull['features'];
    currentProduct: ProductChildFull;
    setCurrentProduct: (color: string, capacity?: string) => void;
    isRenewal: boolean;
    openEnergyLabelModal?: () => void;
    viewer3d?: Product3dViewer;
    openViewer3dModal?: () => void;
    isViewer3dModalOpen?: boolean;
};

export function ProductPreview({
    type,
    features,
    currentProduct,
    setCurrentProduct,
    isRenewal,
    openEnergyLabelModal,
    viewer3d,
    openViewer3dModal,
    isViewer3dModalOpen = false,
}: Readonly<ProductPreviewProps>) {
    const [previewIndex, setPreviewIndex] = useState<number>(viewer3d ? -1 : 0);
    const [thumbnailIndex, setThumbnailIndex] = useState<number>(0);
    const inlineViewerRef = useRef<HTMLIFrameElement>(null);
    const [readyViewerUrl, setReadyViewerUrl] = useState<string>();

    const previews = currentProduct.medias.map((media, index) => ({
        media,
        index: viewer3d && index === 0 ? -1 : index,
    }));

    const isPhone = useMemo(() => type === WallType.PHONE || type === WallType.SALES_PHONE, [type]);

    const handleChangePreview = (index: number) => {
        setPreviewIndex(index);
        sendProductPreviewTracking(isRenewal);
    };

    const handleOpenViewer3d = useCallback(() => {
        if (openViewer3dModal) {
            sendViewer3dOpeningTracking(isRenewal);
            openViewer3dModal();
        }
    }, [isRenewal, openViewer3dModal]);

    const inlineViewerUrl = useMemo(() => {
        if (!viewer3d) return undefined;
        const [path, query] = viewer3d.url.split('?');
        const params = new URLSearchParams(query);
        params.set('colors', currentProduct.color);
        params.set('color', currentProduct.color);
        params.set('presentation', 'inline');
        return `${path}?${params}`;
    }, [viewer3d, currentProduct.color]);

    const isViewerReady = readyViewerUrl === inlineViewerUrl;

    useEffect(() => {
        const handleMessage = ({ origin, source, data }: MessageEvent<unknown>) => {
            if (
                origin !== globalThis.location.origin ||
                source !== inlineViewerRef.current?.contentWindow ||
                typeof data !== 'object' ||
                data === null ||
                !('type' in data)
            ) {
                return;
            }
            if (data.type === PRODUCT_3D_VIEWER_MESSAGES.OPEN_MODAL) {
                handleOpenViewer3d();
            } else if ('url' in data && data.url === inlineViewerUrl) {
                if (data.type === PRODUCT_3D_VIEWER_MESSAGES.READY) {
                    setReadyViewerUrl(inlineViewerUrl);
                } else if (data.type === PRODUCT_3D_VIEWER_MESSAGES.LOADING) {
                    setReadyViewerUrl(undefined);
                }
            }
        };

        globalThis.addEventListener('message', handleMessage);
        return () => globalThis.removeEventListener('message', handleMessage);
    }, [handleOpenViewer3d, inlineViewerUrl]);

    const currentPreview = useMemo(
        () => currentProduct.medias[previewIndex] ?? (currentProduct.medias[0] as string),
        [currentProduct.gencode, previewIndex],
    );

    return (
        <div className='is-sticky-top-desktop product-preview'>
            <Columns align={Align.CENTER} mobile marginless>
                <div className={classNames('is-relative', viewer3d ? 'product-preview-gallery' : '')}>
                    <div className='product-thumbnail'>
                        <Column narrow verticalAlign={Align.CENTER}>
                            <FlexBox
                                align={Align.CENTER}
                                direction={DirectionEnum.COLUMN}
                                gap={GapSize.TWO}
                                className={viewer3d ? 'product-preview-thumbnails' : undefined}
                            >
                                <Button
                                    iconName={IconName.ARROW_UP}
                                    className={classNames('button-clear', thumbnailIndex > 0 ? '' : 'is-invisible')}
                                    onClick={() => setThumbnailIndex(thumbnailIndex - 1)}
                                >
                                    <Text markup={TextMarkup.SPAN} className='sr-only'>
                                        Voir les images précédentes
                                    </Text>
                                </Button>
                                {previews.map(
                                    ({ media, index }, position) =>
                                        position >= thumbnailIndex &&
                                        position <= thumbnailIndex + 2 && (
                                            <Button
                                                key={`${media}-${index}`}
                                                className='button-clear h-auto'
                                                aria-label={index === -1 ? 'Vue 3D' : `Voir la photo ${index + 1}`}
                                                aria-pressed={previewIndex === index}
                                                onClick={() => handleChangePreview(index)}
                                            >
                                                <ImageThumbor
                                                    src={`/${media}`}
                                                    alt=''
                                                    width={40}
                                                    height={isPhone ? 80 : 35}
                                                    position={0}
                                                    fetchPriority='high'
                                                />
                                            </Button>
                                        ),
                                )}
                                <Button
                                    iconName={IconName.ARROW_DOWN}
                                    className={classNames(
                                        'button-clear',
                                        thumbnailIndex < previews.length - 3 ? '' : 'is-invisible',
                                    )}
                                    onClick={() => setThumbnailIndex(thumbnailIndex + 1)}
                                >
                                    <Text markup={TextMarkup.SPAN} className='sr-only'>
                                        Voir les images suivantes
                                    </Text>
                                </Button>
                            </FlexBox>
                        </Column>
                    </div>
                    <Column className={viewer3d ? 'product-preview-stage' : undefined}>
                        {viewer3d && previewIndex === -1 && !isViewer3dModalOpen && (
                            <iframe
                                ref={inlineViewerRef}
                                src={inlineViewerUrl}
                                title={`${viewer3d.name} en 3D — ${currentProduct.color}`}
                                className={classNames(
                                    'viewer-3d-frame viewer-3d-inline',
                                    isViewerReady ? 'is-ready' : '',
                                )}
                                loading='lazy'
                                aria-describedby={isViewerReady ? 'product-preview-3d-instruction' : undefined}
                            />
                        )}
                        {(!viewer3d || previewIndex !== -1 || isViewer3dModalOpen || !isViewerReady) && (
                            <ImageThumbor
                                src={`/${currentPreview}`}
                                alt={`Photo du produit ${currentProduct.parentTitle} - vue ${Math.max(previewIndex, 0) + 1}`}
                                aria-hidden='true'
                                width={200}
                                height={isPhone ? 400 : 200}
                                id='productPreview'
                                position={0}
                                fetchPriority='high'
                            />
                        )}
                        {viewer3d && previewIndex === -1 && !isViewer3dModalOpen && isViewerReady && (
                            <Text
                                markup={TextMarkup.P}
                                id='product-preview-3d-instruction'
                                className='product-preview-instruction'
                            >
                                Glissez horizontalement pour faire tourner
                            </Text>
                        )}
                        {viewer3d && previewIndex === -1 && openViewer3dModal && (
                            <Button
                                className='button-clear product-preview-expand'
                                iconName={IconName.PLUS_CIRCLE}
                                aria-label='Agrandir la vue 3D'
                                onClick={handleOpenViewer3d}
                            />
                        )}
                    </Column>
                </div>
            </Columns>

            <Spacer size={SpacerSize.FOUR} />
            <View flexable justify={Justify.CENTER} className='gap-medium'>
                {features.color.map(({ name, value }) => (
                    <Color
                        key={name}
                        color={value}
                        isFocus={currentProduct.color === name}
                        onClick={() => setCurrentProduct(name, currentProduct.capacity)}
                    />
                ))}
            </View>
            <Spacer size={SpacerSize.FOUR} />
            <View flexable justify={Justify.CENTER} className='gap-medium'>
                {currentProduct.characteristics.energyLabel && (
                    <EnergyLabel
                        rating={currentProduct.characteristics.energyLabel.label}
                        onClick={openEnergyLabelModal}
                    />
                )}
                <Das das={currentProduct.das} />
            </View>
        </div>
    );
}
